import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import styled from '@emotion/styled';
import ServicePageTemplate from 'presentations/views/components/templates/ServicePageTemplate';
import {
  black,
  gray01,
  gray05,
  gray06,
  primary,
  primaryVariantDark,
  white,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import Headline from 'presentations/views/components/atoms/Headline';
import IntegrationList from 'presentations/views/components/organisms/service/ServiceIntegrationList';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import {
  contactPath,
  productEmbedPath,
  resourcePath,
} from 'presentations/routes/path';
import ContentItem4 from 'presentations/views/components/molecules/ServiceContentItem4';
import { useRouter } from 'next/router';
import {
  developerApiAccountPath,
  developerApiWebhookPath,
  developerCenterApiReferencePath,
  developerCenterPath,
} from 'presentations/routes/path/developer';
import Header from './Header';

interface Props {
  viewer: ViewerItem | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  return (
    <ServicePageTemplate isVisibleSupport={true} breadcrumbs={[]}>
      <Page>
        <LightBg />
        <MainVisual>
          <HeaderContainer>
            <Header viewer={props.viewer} />
          </HeaderContainer>
          <ServiceMainVisual>
            <Title variant="h1">{t('copy')}</Title>
            <Description>{t('lead')}</Description>
            <ButtonList>
              <ButtonListItem>
                <DevelopButton href={developerCenterPath()}>
                  {t('View documentation')}
                </DevelopButton>
              </ButtonListItem>
              <ButtonListItem>
                <ContactButton href={contactPath()}>
                  {t('Contact')}
                </ContactButton>
              </ButtonListItem>
            </ButtonList>
          </ServiceMainVisual>

          <MainContainer>
            <SectionHeadlineContainer>
              <SectionHeadlineContent>
                <SectionHeadline variant="h2">
                  {t('main title')}
                </SectionHeadline>
                <SectionTitleEn>{t('main description')}</SectionTitleEn>
              </SectionHeadlineContent>
            </SectionHeadlineContainer>

            <ListContainer2>
              <ListItem2>
                <ContentItem4
                  link={developerCenterApiReferencePath()}
                  imageUrl="pages/developer/api.svg"
                  imageWidth={96}
                  imageHeight={80}
                  title={t('item 1 title')}
                  description={t('item 1 description')}
                />
              </ListItem2>
              <ListItem2>
                <ContentItem4
                  link={productEmbedPath()}
                  imageUrl="pages/developer/embed.svg"
                  imageWidth={96}
                  imageHeight={80}
                  title={t('item 2 title')}
                  description={t('item 2 description')}
                />
              </ListItem2>
              <ListItem2>
                <ContentItem4
                  link={developerApiWebhookPath(router.locale)}
                  imageUrl="pages/developer/webhook.svg"
                  imageWidth={80}
                  imageHeight={80}
                  title={t('item 3 title')}
                  description={t('item 3 description')}
                />
              </ListItem2>
              <ListItem2>
                <ContentItem4
                  link={developerApiAccountPath(router.locale)}
                  imageUrl="pages/developer/account.svg"
                  imageWidth={80}
                  imageHeight={80}
                  title={t('item 4 title')}
                  description={t('item 4 description')}
                />
              </ListItem2>
            </ListContainer2>
          </MainContainer>
        </MainVisual>
        <OddSectionContainer>
          <SectionContainer>
            <SectionHeadlineContainer>
              <SectionHeadlineContent>
                <SectionHeadline variant="h2">{t('Showcase')}</SectionHeadline>
                <SectionTitleEn>{t('Showcase description')}</SectionTitleEn>
              </SectionHeadlineContent>
            </SectionHeadlineContainer>
            <ShowcaseList>
              <ShowcaseListItem
                href={resourcePath('usecases', 'kiyo-learning')}
              >
                <ShowcaseImageContainer>
                  <ShowcaseImage
                    width={150}
                    height={63}
                    src="pages/top/logos/logo_kiyo.png"
                    alt="KIYO LEARNING"
                  />
                </ShowcaseImageContainer>
                <ShowcaseContent>
                  <ShowcaseText>{t('ShowcaseText')}</ShowcaseText>
                  <ShowcaseTitle>KIYO LEARNING</ShowcaseTitle>
                </ShowcaseContent>
              </ShowcaseListItem>
            </ShowcaseList>
          </SectionContainer>
        </OddSectionContainer>

        <SectionContainer>
          <SectionContainer>
            <IntegrationList />
          </SectionContainer>
        </SectionContainer>
      </Page>
    </ServicePageTemplate>
  );
}

const Page = styled.div``;
const MainVisual = styled.div`
  position: relative;
  overflow: hidden;
  background: url(${imagePath('pages/developer/mv_bg_dot.png', {
      width: 1344,
      height: 1176,
      dpr: 2,
    })})
    repeat center top;
  background-size: 1344px 1176px;
  padding-bottom: ${spacing(64)};
`;
const MainContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  ${mediaMax.tablet} {
    margin: 0 ${spacing(12)};
  }
`;
const LightBg = styled.div`
  position: absolute;
  top: 0;
  left; 0;
  right: 0;
  z-index: 0;

  background: url(${imagePath('pages/developer/mv_bg.png', {
    width: 759,
    height: 535,
    dpr: 2,
  })}) no-repeat center top;
  background-size: 759px 535px;
  ${mediaMax.tablet} {
    background-size: 100%;
  }
  width: 100%;
  height: 535px;
`;
const HeaderContainer = styled.div`
  background-color: rgba(255, 255, 255, 0.2);
`;
const OddSectionContainer = styled.div`
  background-color: ${gray06};
`;
const SectionContainer = styled.div`
  max-width: 1080px;
  margin: 0 auto;
  padding: ${spacing(80)} ${spacing(16)} ${spacing(56)};
  ${mediaMax.tablet} {
    padding: ${spacing(48)} ${spacing(16)};
  }
  border-bottom: 1px solid ${gray05};
  :last-of-type {
    border-bottom: 0;
  }
`;
const ServiceMainVisual = styled.div`
  text-align: center;
  padding: ${spacing(64)} ${spacing(32)} ${spacing(140)};

  ${mediaMax.tablet} {
    padding: 40px 40px 80px;
  }

  ${mediaMax.tablet} {
    padding: 24px 0 80px;
  }

  ${mediaMax.smartphone} {
    padding: 40px 0 40px;
  }
`;

const Title = styled(Headline)`
  display: inline-block;
  font-size: 64px;
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
  letter-spacing: -0.04em;

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: 6vw;
  }
`;
const Description = styled.p`
  margin-bottom: ${spacing(40)};
  font-size: ${fontSize(16)};
  color: ${gray01};
  letter-spacing: -0.02em;

  ${mediaMax.tablet} {
    font-size: 3vw;
    margin-bottom: ${spacing(20)};
  }

  br {
    display: none;

    ${mediaMax.tablet} {
      display: block;
    }
  }
  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: ${fontSize(14)};
  }
`;
const ButtonList = styled.ul`
  /* margin-bottom: ${spacing(64)}; */
  position: relative;

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(32)};
  }
`;
const ButtonListItem = styled.li`
  display: inline-block;
  margin-right: ${spacing(32)};
  :last-of-type {
    margin-right: 0;
  }
`;
const ContactButton = styled.a`
  // Contactに飛ぶためaタグ
  display: block;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  border-radius: ${borderRadius(12)};
  background: ${primary};
  color: ${white};
  font-weight: ${fontWeight('bold')};
  padding: ${spacing(12)} ${spacing(40)};

  &:hover {
    background: ${primaryVariantDark};
  }

  ${mediaMax.tablet} {
    padding: ${spacing(8)} ${spacing(16)};
    border-radius: ${borderRadius(6)};
  }
`;
const DevelopButton = styled(ContactButton)`
  background: ${black};
  &:hover {
    background: ${gray01};
  }
`;
const ListContainer2 = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${spacing(64)};
  ${mediaMax.tablet} {
    display: block;
  }
`;

const ListItem2 = styled.div`
  color: ${black};
  display: block;
  width: 32%;
  height: auto;
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    width: 100%;
    margin-bottom: ${spacing(24)};
  }
`;
const SectionHeadlineContent = styled.div``;
const SectionHeadline = styled(Headline)`
  text-align: center;
  font-size: ${fontSize(32)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  /* background: linear-gradient(
    73.82deg,
    #11d79b -24.74%,
    #11bdd7 16.25%,
    #0054d1 79.08%,
    #8e00d1 113.33%
  );
  -webkit-background-clip: text;
  color: transparent; */
  letter-spacing: -0.04em;
  ${mediaMax.tablet} {
    font-size: 6vw;
  }
`;
const SectionTitleEn = styled.div`
  color: ${primary};
  text-align: center;
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(16)};
  margin-top: 8px;
`;

const SectionHeadlineContainer = styled.div`
  margin-bottom: ${spacing(48)};
`;

const ShowcaseList = styled.div``;
const ShowcaseListItem = styled(Link)`
  color: ${black};
  height: 100%;
  display: flex;
  box-shadow: 0px 10px 30px rgba(102, 102, 102, 0.15);
  border-radius: ${borderRadius(16)};
  padding: ${spacing(48)} ${spacing(64)};
  background-color: ${white};
  ${mediaMax.tablet} {
    width: auto;
    flex-direction: column;
    padding: ${spacing(48)} ${spacing(48)};
  }
`;
const ShowcaseImageContainer = styled.div`
  margin-right: ${spacing(64)};
  ${mediaMax.tablet} {
    display: flex;
    justify-content: center;
    margin-right: 0;
    margin-bottom: ${spacing(32)};
  }
`;
const ShowcaseContent = styled.div`
  flex: 1;
`;
const ShowcaseTitle = styled.div``;
const ShowcaseText = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(8)};
`;
const ShowcaseImage = styled(Image)`
  margin: ${spacing(8)} 0;
  max-height: 36px;
  width: auto;
`;
